import React, { useState } from "react";
import NoData from "./../../assets/images/NoDataTableImage.svg";

interface FunnelData {
  [key: string]: number;
}

interface FunnelChartProps {
  data: FunnelData;
}

const COLORS = ["#841C39", "#000000"];

const FunnelChart: React.FC<FunnelChartProps> = ({ data }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const [hovered, setHovered] = useState<number | null>(null);
  const total = entries.reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
  const n = entries.length;

  if (!n) {
    return (
      <div className="flex h-[220px] items-center justify-center">
        <img src={NoData} alt="No data" className="h-[100px] w-auto" />
      </div>
    );
  }

  const rowH = 52;
  const gap = 10;
  const vbW = 420;
  const cx = vbW / 2;
  const topW = 400;
  const bottomW = 240;
  const shrink = n > 1 ? (topW - bottomW) / n : 0;
  const svgH = n * rowH + (n - 1) * gap;

  return (
    <div className="flex h-full min-h-[300px] flex-col">
      <div className="relative mx-auto w-full max-w-[460px] flex-1 px-1 py-3">
        <svg
          viewBox={`0 0 ${vbW} ${svgH}`}
          className="mx-auto block h-auto w-full overflow-visible"
          role="img"
          aria-label="Opportunity pipeline funnel"
        >
          <defs>
            {entries.map((_, i) => (
              <filter
                key={`shadow-${i}`}
                id={`funnel-shadow-${i}`}
                x="-20%"
                y="-20%"
                width="140%"
                height="160%"
              >
                <feDropShadow
                  dx="0"
                  dy="2"
                  stdDeviation="3"
                  floodColor="#0f172a"
                  floodOpacity="0.12"
                />
              </filter>
            ))}
          </defs>

          {entries.map(([key, value], i) => {
            const pct = total ? Math.round((Number(value) / total) * 100) : 0;
            const wTop = topW - i * shrink;
            const wBot = topW - (i + 1) * shrink;
            const y = i * (rowH + gap);
            const x1 = cx - wTop / 2;
            const x2 = cx + wTop / 2;
            const x3 = cx + wBot / 2;
            const x4 = cx - wBot / 2;
            const isHover = hovered === i;
            const label = String(key);
            const shortLabel =
              label.length > 16 ? `${label.slice(0, 14)}…` : label;

            // Rounded look via slight inset on corners of polygon
            const path = `M ${x1 + 6} ${y}
              L ${x2 - 6} ${y}
              Q ${x2} ${y} ${x2} ${y + 6}
              L ${x3} ${y + rowH - 6}
              Q ${x3} ${y + rowH} ${x3 - 6} ${y + rowH}
              L ${x4 + 6} ${y + rowH}
              Q ${x4} ${y + rowH} ${x4} ${y + rowH - 6}
              L ${x1} ${y + 6}
              Q ${x1} ${y} ${x1 + 6} ${y} Z`;

            return (
              <g
                key={`${key}-${i}`}
                filter={`url(#funnel-shadow-${i})`}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: "pointer" }}
                opacity={isHover ? 1 : 0.96}
              >
                <path d={path} fill={COLORS[i % COLORS.length]} />
                <text
                  x={x1 + 22}
                  y={y + rowH / 2 + 1}
                  dominantBaseline="middle"
                  fill="#ffffff"
                  fontSize="14"
                  fontWeight="600"
                  fontFamily="Helvetica Neue, Inter, system-ui, sans-serif"
                >
                  {shortLabel}
                </text>
                <text
                  x={x2 - 22}
                  y={y + rowH / 2 + 1}
                  dominantBaseline="middle"
                  textAnchor="end"
                  fill="#ffffff"
                  fontSize="14"
                  fontWeight="600"
                  fontFamily="Helvetica Neue, Inter, system-ui, sans-serif"
                >
                  {`${Number(value).toLocaleString()} (${pct}%)`}
                </text>
              </g>
            );
          })}
        </svg>

        {hovered !== null && entries[hovered] && (
          <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-1 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#111827] px-3 py-1.5 text-xs font-medium text-white shadow-lg">
            {entries[hovered][0]}:{" "}
            {Number(entries[hovered][1]).toLocaleString()} (
            {total
              ? Math.round((Number(entries[hovered][1]) / total) * 100)
              : 0}
            %)
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-[#EEF0F5] pt-4">
        <span className="text-sm font-semibold text-[#111827]">
          Total Opportunities
        </span>
        <span className="text-[26px] font-bold leading-none tracking-tight text-[#A02448]">
          {total.toLocaleString()}
        </span>
      </div>
    </div>
  );
};

export default FunnelChart;
